import React, { useState, useEffect } from 'react';
import { getDashboardSummary } from '../../api/DashboardAPI';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(num);
};

const formatQuantity = (value) => {
    const num = parseFloat(value) || 0;
    return num.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

// --- Updated Smart Bar Chart ---
const SimpleBarChart = ({ data }) => {
    const [hoveredValue, setHoveredValue] = useState(null);
    const [hoveredIndex, setHoveredIndex] = useState(null);

    if (!data || data.length === 0) return <div className="text-muted text-center py-5">No data available</div>;

    const values = data.map(d => d.value);
    const max = Math.max(...values) || 1;
    const avg = values.reduce((a, b) => a + b, 0) / values.length || 0;

    let scaleMax = max;
    if (max > (avg * 3) && avg > 0) {
        scaleMax = avg * 3;
    }

    const barWidthPercent = (100 / data.length) - 0.5;

    return (
        <div className="position-relative h-100 w-100" style={{ minHeight: '200px' }}>

            {hoveredValue !== null && (
                <div
                    className="position-absolute bg-dark text-white px-2 py-1 rounded shadow-sm"
                    style={{
                        top: '-30px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 10,
                        fontSize: '0.75rem'
                    }}
                >
                    {hoveredValue}
                </div>
            )}

            <div className="d-flex align-items-end justify-content-between h-100 w-100 pt-3 px-1">
                {data.map((item, index) => {
                    let heightPercentage = (item.value / scaleMax) * 100;
                    const isCapped = heightPercentage > 100;
                    if (isCapped) heightPercentage = 100;

                    const showLabel = data.length > 15 ? index % 3 === 0 : true;

                    return (
                        <div
                            key={index}
                            className="d-flex flex-column align-items-center position-relative"
                            style={{ width: `${barWidthPercent}%`, height: '100%' }}
                            onMouseEnter={() => { setHoveredValue(`${item.date}: ₹${item.value.toLocaleString()}`); setHoveredIndex(index); }}
                            onMouseLeave={() => { setHoveredValue(null); setHoveredIndex(null); }}
                        >
                            <div className="d-flex flex-column justify-content-end h-100 w-100">
                                <div
                                    className="w-100" // Removed rounded-top
                                    style={{
                                        height: `${Math.max(heightPercentage, 2)}%`,
                                        // Use requested RGB color. Use a darker shade if capped (outlier)
                                        backgroundColor: item.value > 0 ? (isCapped ? '#0a4b52' : 'rgb(15, 98, 106)') : '#f1f3f5',
                                        transition: 'height 0.3s ease',
                                        opacity: hoveredIndex === index ? 1 : 0.9,
                                        borderRadius: '0' // Ensure rectangular corners
                                    }}
                                >
                                    {isCapped && (
                                        <div className="w-100 text-center text-white" style={{ fontSize: '0.5rem', lineHeight: '10px' }}>
                                            <i className="ti ti-chevrons-up"></i>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div style={{ height: '20px', position: 'relative', width: '100%' }}>
                                {showLabel && (
                                    <small className="text-muted position-absolute w-200 text-center" style={{ fontSize: '0.6rem', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}>
                                        {item.date}
                                    </small>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};



// --- NEW Native SVG Line Chart with Y-Axis and Point Labels ---
const SimpleLineChart = ({ data }) => {
    if (!data || data.length === 0) return <div className="text-muted text-center py-5">No data available</div>;

    const values = data.map(d => parseFloat(d.value) || 0);
    const max = Math.max(...values) || 1;
    const avg = values.reduce((a, b) => a + b, 0) / values.length || 0;

    let scaleMax = max;
    // Cap outliers to prevent the chart from squishing normal days
    if (max > (avg * 3) && avg > 0) {
        scaleMax = avg * 3;
    }
    
    // Round scaleMax to a clean top number for the Y-Axis
    const magnitude = Math.pow(10, Math.floor(Math.log10(scaleMax || 1)));
    scaleMax = Math.ceil(scaleMax / magnitude) * magnitude;
    if (scaleMax < 10) scaleMax = 10;

    // SVG Dimensions & Padding
    const viewBoxWidth = 800;
    const viewBoxHeight = 280;
    
    // Adjust padding to make room for Y-axis labels and top point labels
    const paddingLeft = 70;  
    const paddingRight = 30;
    const paddingTop = 40;   
    const paddingBottom = 40; 

    const chartWidth = viewBoxWidth - paddingLeft - paddingRight;
    const chartHeight = viewBoxHeight - paddingTop - paddingBottom;

    // Calculate coordinates for each point
    const points = data.map((item, index) => {
        const x = paddingLeft + (index * (chartWidth / (data.length - 1)));
        let val = parseFloat(item.value) || 0;
        let heightVal = val > scaleMax ? scaleMax : val;
        const y = paddingTop + chartHeight - ((heightVal / scaleMax) * chartHeight);
        return { x, y, value: val, date: item.date, isCapped: val > scaleMax };
    });

    const polylinePoints = points.map(p => `${p.x},${p.y}`).join(' ');

    // Calculate 5 horizontal Y-axis grid lines (0%, 25%, 50%, 75%, 100%)
    const gridLines = [0, 1, 2, 3, 4].map(i => {
        const y = paddingTop + chartHeight - (i * (chartHeight / 4));
        const val = (scaleMax / 4) * i;
        return { y, val };
    });

    // Formatting Helpers
    const formatYAxis = (val) => {
        if (val >= 100000) return '₹' + (val / 100000).toFixed(2) + 'L';
        if (val >= 1000) return '₹' + (val / 1000).toFixed(1) + 'k';
        return '₹' + Math.round(val);
    };

    const formatPoint = (val) => {
        if (val === 0) return ''; // Hide 0 labels to keep bottom clean
        return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(val);
    };

    return (
        <div className="position-relative w-100 h-100" style={{ minHeight: '280px' }}>
            <svg viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`} className="w-100 h-100" style={{ overflow: 'visible' }}>
                
                {/* Y-Axis Grid Lines & Labels */}
                {gridLines.map((grid, index) => (
                    <g key={`grid-${index}`}>
                        <line 
                            x1={paddingLeft} 
                            y1={grid.y} 
                            x2={viewBoxWidth - paddingRight} 
                            y2={grid.y} 
                            stroke={index === 0 ? "#dee2e6" : "#f1f3f5"} // Darker line for 0 base
                            strokeWidth="2" 
                            strokeDasharray={index === 0 ? "0" : "5,5"} 
                        />
                        <text 
                            x={paddingLeft - 10} 
                            y={grid.y + 4} 
                            fontSize="13" 
                            fill="#6c757d" 
                            textAnchor="end"
                        >
                            {formatYAxis(grid.val)}
                        </text>
                    </g>
                ))}

                {/* The Trend Line */}
                <polyline
                    fill="none"
                    stroke="rgb(15, 98, 106)"
                    strokeWidth="3"
                    points={polylinePoints}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                />

                {/* Data Points and Labels */}
                {points.map((p, index) => (
                    <g key={`point-${index}`}>
                        {/* Visible Circle */}
                        <circle
                            cx={p.x}
                            cy={p.y}
                            r="5"
                            fill={p.isCapped ? "#ff512f" : "rgb(15, 98, 106)"}
                            stroke="#fff"
                            strokeWidth="2"
                        />
                        
                        {/* Value Label Placed Above Point */}
                        <text 
                            x={p.x} 
                            y={p.y - 12} // Adjusted to sit cleanly above the point
                            fontSize="13" 
                            fill={p.isCapped ? "#ff512f" : "rgb(15, 98, 106)"} 
                            fontWeight="600"
                            textAnchor="middle"
                        >
                            {formatPoint(p.value)}
                        </text>

                        {/* X-Axis Date Label */}
                        <text
                            x={p.x}
                            y={viewBoxHeight - 15}
                            fontSize="13"
                            fill="#6c757d"
                            textAnchor="middle"
                        >
                            {p.date}
                        </text>
                    </g>
                ))}
            </svg>
        </div>
    );
};

const FranchiseeUserDashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState({
        total_sales_month: 0,
        total_sales_today: 0,
        total_receivables: 0,
        total_payables: 0,
        top_products_qty_day: [],
        top_products_value_day: [],
        recent_sales: [],
        recent_purchases: [],
        low_stock_items: [],
        chart_data: [],
        period: 'This Month'
    });

    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        getDashboardSummary((response) => {
            if (isMounted && !response.error) {
                setSummary(response);
            }
            setLoading(false);
        });
        return () => { isMounted = false; };
    }, []);

    // Custom Styles
    const getGradientStyle = (type) => {
        const base = {
            border: 'none',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            color: '#fff',
            overflow: 'hidden',
            position: 'relative'
        };

        switch (type) {
            case 'primary': return { ...base, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' };
            case 'info': return { ...base, background: 'linear-gradient(135deg, #0ba360 0%, #3cba92 100%)' };
            case 'success': return { ...base, background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' };
            case 'danger': return { ...base, background: 'linear-gradient(135deg, #ff512f 0%, #dd2476 100%)' };
            default: return { ...base, backgroundColor: '#fff', color: '#333' };
        }
    };

    const iconCircleStyle = {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        width: '45px',
        height: '45px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.2rem',
        marginBottom: '0.5rem',
        backdropFilter: 'blur(5px)'
    };

    const tableHeaderStyle = {
        backgroundColor: '#f8f9fa',
        borderBottom: '1px solid #e9ecef',
        padding: '1.25rem 1.5rem',
        borderRadius: '12px 12px 0 0'
    };

    return (
        <>
            <main className="bg-sale h-100">
                <div className="container-fluid pb-5">
                    {/* Header */}
                    <div className="d-flex justify-content-between align-items-center mt-4 mb-4">
                        <div>
                            <h4 className="mb-1 text-dark fw-bold">Dashboard</h4>
                            <p className="text-success mb-0 f-s-13">Overview of your business performance.</p>
                        </div>
                        <div className="d-none d-md-block">
                            <span className="badge btn-red border px-3 py-2 fw-normal shadow-sm">
                                <i className="ti ti-calendar me-2"></i> {new Date().toDateString()}
                            </span>
                        </div>
                    </div>

                    {loading && <div className="loader_box"><div className="loader_14"></div></div>}

                    {!loading && (
                        <>
                            {/* --- Row 1: Colorful Stat Cards --- */}
                            <div className="row g-3 mb-4">


                                <div className="col-6 col-md-3">
                                    <div className="card h-100" >
                                        <div className="card-body p-4">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <p className="mb-1 f-s-13 fw-500 opacity-75"><b>Sales ({summary.period}) </b></p>
                                                    <h3 className="fw-bold mb-0 text-success">{formatCurrency(summary.total_sales_month)}</h3>
                                                </div>
                                                <div style={iconCircleStyle}><i className="ti ti-calendar-stats" /></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-6 col-md-3">
                                    <div className="card h-100">
                                        <div className="card-body p-4">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <p className="mb-1 f-s-13 fw-500 opacity-75"><b> Sales (Today)</b></p>
                                                    <h3 className="fw-bold mb-0 text-success">{formatCurrency(summary.total_sales_today)}</h3>
                                                </div>
                                                <div style={iconCircleStyle}><i className="ti ti-clock-hour-4" /></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {/* <div className="col-6 col-md-3">
                                    <div className="card h-100" >
                                        <div className="card-body p-4">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <p className="mb-1 f-s-13 fw-500 opacity-75"><b>Total Receivables</b></p>
                                                    <h3 className="fw-bold mb-0 text-danger">{formatCurrency(summary.total_receivables)}</h3>
                                                </div>
                                                <div style={iconCircleStyle}><i className="ti ti-arrow-down-left" /></div>
                                            </div>
                                        </div>
                                    </div>
                                </div> */}
                                {/* <div className="col-6 col-md-3">
                                    <div className="card h-100" >
                                        <div className="card-body p-4">
                                            <div className="d-flex justify-content-between align-items-start">
                                                <div>
                                                    <p className="mb-1 f-s-13 fw-500 opacity-75"><b>Total Payables</b></p>
                                                    <h3 className="fw-bold mb-0 text-danger">{formatCurrency(summary.total_payables)}</h3>
                                                </div>
                                                <div style={iconCircleStyle}><i className="ti ti-arrow-up-right" /></div>
                                            </div>
                                        </div>
                                    </div>
                                </div> */}
                            </div>

                            {/* --- Row 2: Sales Graph & Recent Sales --- */}
                            <div className="row g-3 mb-4">
                                <div className="col-lg-8 col-md-12">
                                    <div className="card h-100  shadow-sm" style={{ borderRadius: '12px' }}>
                                        <div style={tableHeaderStyle} className="gradient-1-bg d-flex justify-content-between align-items-center">
                                            <h6 className="mb-0 fw-bold text-white">Sales Trend</h6>
                                            {/* Changed text from 'Last 30 Days' to 'Last 10 Days' */}
                                            <span className="badge bg-white text-muted border">Last 10 Days</span>
                                        </div>
                                        <div className="card-body px-4 pb-4 pt-4">
                                            {/* Implemented the new SVG Line Chart */}
                                            <SimpleLineChart data={summary.chart_data} />
                                        </div>
                                    </div>
                                </div>

                                <div className="col-lg-4 col-md-12">
                                    <div className="card h-100  shadow-sm" style={{ borderRadius: '12px' }}>
                                        <div className="gradient-2-bg" style={tableHeaderStyle}>
                                            <h6 className="mb-0 fw-bold text-white">Recent Sales</h6>
                                        </div>
                                        <div className="card-body p-0">
                                            <div className="table-responsive px-4 pb-4">
                                                <table className="table table-hover align-middle mb-0 mt-2" style={{ fontSize: '0.9rem' }}>
                                                    <thead className="text-muted fw-light">
                                                        <tr><th className="fw-normal ps-0 ">Invoice</th><th className="text-end fw-normal pe-0 ">Amount</th></tr>
                                                    </thead>
                                                    <tbody>
                                                        {summary.recent_sales.length > 0 ? summary.recent_sales.map(inv => (
                                                            <tr key={`sale-${inv.id}`} style={{ cursor: 'pointer' }} onClick={() => navigate(`/franchisee_user/sales/sales-invoices/edit/${inv.id}`)}>
                                                                <td className="ps-0 py-3">
                                                                    <div className="fw-500 text-dark">{inv.invoice_number}</div>
                                                                    <small className="text-muted">{inv.party?.name}</small>
                                                                </td>
                                                                <td className="text-end pe-0 py-3 fw-bold text-success">{formatCurrency(inv.total_amount)}</td>
                                                            </tr>
                                                        )) : (
                                                            <tr><td colSpan="2" className="text-center text-muted py-4">No recent sales.</td></tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* --- Row 3: Top Products & Low Stock --- */}
                            <div className="row g-3">


                                <div className="card col-sm-6 col-lg-4 col-xxl-4 ">
                                    <ul className=" box-list mb-4">
                                        <li className="b-s-4-primary gradient-4-bg ">
                                            <h5 className="text-white">Top Products Today (Qty)</h5>
                                        </li>

                                        {summary.top_products_qty_day.length > 0 ? summary.top_products_qty_day.map((prod, i) => (
                                        <li key={`qty-${i}`} className="d-flex align-items-center justify-between gap-2">
                                            <div className="b-1-light bg-primary-200 p-1 h-40 w-40 d-flex-center b-r-12 flex-shrink-0 overflow-hidden box-list-img">
                                                <img alt="Smart Watch" className="img-fluid" src={`${process.env.REACT_APP_API_BASE_URL}/storage/${prod.image_path}`} />
                                            </div>
                                            <div className="mg-s-45 flex-grow-1">
                                                <h6 className="mb-0 text-dark-800 f-w-500 txt-ellipsis-1">{prod.name}</h6>
                                                <p className="text-secondary-800 mb-0">{prod.base_uom_name}</p>
                                            </div>
                                            <span className="badge bg-light-primary">{prod.total_quantity}</span>
                                        </li>
                                         )) : (
                                                 <span className="badge bg-light-primary"></span>
                                            )}
                                      
                                    </ul>
                                </div>



                                {/* <div className="col-lg-4 col-md-6">
                                    <div className="card h-100  shadow-sm" style={{ borderRadius: '12px' }}>
                                        <div style={tableHeaderStyle}>
                                            <h6 className="mb-0 fw-bold text-dark">Top Products (Qty)</h6>
                                        </div>
                                        <div className="card-body p-0">
                                            <div className="table-responsive px-4 pb-4">
                                                <table className="table table-sm table-borderless mb-0 mt-3 align-middle" style={{ fontSize: '0.9rem' }}>
                                                    <tbody>
                                                        {summary.top_products_qty_day.length > 0 ? summary.top_products_qty_day.map((prod, i) => (
                                                            <tr key={`qty-${i}`}>
                                                                <td className="ps-0 py-2">
                                                                    <div className="d-flex align-items-center">
                                                                        <span className="badge bg-light text-dark me-2 border">{i + 1}</span>
                                                                        <span className="text-dark fw-500 text-truncate" style={{ maxWidth: '150px' }} title={prod.name}>{prod.name}</span>
                                                                    </div>
                                                                </td>
                                                                <td className="text-end pe-0 py-2 fw-bold text-primary">{formatQuantity(prod.total_quantity)}</td>
                                                            </tr>
                                                        )) : (
                                                            <tr><td colSpan="2" className="text-center text-muted py-4">No data available.</td></tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div> */}
                                <div className="col-lg-4 col-md-6">
                                    <div className="card h-100  shadow-sm" style={{ borderRadius: '12px' }}>
                                        <div className="gradient-3-bg" style={tableHeaderStyle}>
                                            <h6 className="mb-0 fw-bold text-white">Top Products (Value)</h6>
                                        </div>
                                        <div className="card-body p-0">
                                            <div className="table-responsive px-4 pb-4">
                                                <table className="table table-sm table-borderless mb-0 mt-3 align-middle" style={{ fontSize: '0.9rem' }}>
                                                    <tbody>
                                                        {summary.top_products_value_day.length > 0 ? summary.top_products_value_day.map((prod, i) => (
                                                            <tr key={`val-${i}`}>
                                                                <td className="ps-0 py-2">
                                                                    <div className="d-flex align-items-center">
                                                                        <span className="badge bg-light text-dark me-2 border">{i + 1}</span>
                                                                        <span className="text-dark fw-500 text-truncate" style={{ maxWidth: '150px' }} title={prod.name}>{prod.name}</span>
                                                                    </div>
                                                                </td>
                                                                <td className="text-end pe-0 py-2 fw-bold text-success">{formatCurrency(prod.total_value)}</td>
                                                            </tr>
                                                        )) : (
                                                            <tr><td colSpan="2" className="text-center text-muted py-4">No data available.</td></tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-4 col-md-12">
                                    <div className="card h-100  shadow-sm" style={{ borderRadius: '12px' }}>
                                        <div className="gradient-2-bg" style={tableHeaderStyle}>
                                            <h6 className="mb-0 fw-bold text-white">Low Stock Alert</h6>
                                        </div>
                                        <div className="card-body p-0">
                                            <div className="table-responsive px-4 pb-4">
                                                <table className="table table-hover align-middle mb-0 mt-2" style={{ fontSize: '0.9rem' }}>
                                                    <thead className="text-muted fw-light">
                                                        <tr><th className="fw-normal ps-0 ">Product</th><th className="text-end fw-normal pe-0 ">Stock</th></tr>
                                                    </thead>
                                                    <tbody>
                                                        {summary.low_stock_items.length > 0 ? summary.low_stock_items.map((item, i) => (
                                                            <tr key={`low-${i}`}>
                                                                <td className="ps-0 py-2 fw-500 text-dark text-truncate" style={{ maxWidth: '180px' }} title={item.name}>{item.name}</td>
                                                                <td className="text-end pe-0 py-2"><span className="badge bg-danger-subtle text-danger border border-danger-subtle">{formatQuantity(item.current_stock)}</span></td>
                                                            </tr>
                                                        )) : (
                                                            <tr><td colSpan="2" className="text-center text-success py-4"><i className="ti ti-circle-check me-1"></i> Stock levels are healthy.</td></tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </main>
        </>
    )
}

export default FranchiseeUserDashboard;